/**
 * React Query hooks for the Leads service (Wave 4 / QFLY-11 PR3 / F8).
 *
 * Patterns mirror ``modules/partners/api/partnerApi.ts`` verbatim so the
 * rest of the SPA reads/writes leads with the same conventions it already
 * uses for partners and contacts:
 *
 *   * Query keys live in {@link leadKeys} so invalidation calls stay in
 *     sync with reads.
 *   * Mutations are admin-only on the BE; the 403 surfaces as a normal
 *     mutation error and the caller (typically a dialog) can show it via
 *     toast / inline banner.
 *   * The BE list endpoint wraps rows in ``{ data, meta }``; the meta
 *     carries the real ``total`` so the SPA can render "page X of N".
 *
 * Stakeholder-locked deviations (2026-06-01):
 *
 *   * **No** ``useSimilarLeads`` — the BE has no ``/similar`` endpoint
 *     (pgvector removed; 40 leads/month doesn't warrant it).
 *   * **No** ``useRestoreLead`` — soft-delete is one-way for v1 (no
 *     ``/restore`` route exists on the BE).
 *   * The nav badge count comes from {@link useNewLeadsBadgeCount}, which
 *     reuses the list endpoint with ``status='new'`` and reads
 *     ``meta.total`` — the BE has no dedicated ``count_only`` flag.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiClient } from '@/shared/api/client';
import type {
  Lead,
  LeadConvertRequest,
  LeadConvertResponse,
  LeadCreatePayload,
  LeadDedupeCheckRequest,
  LeadDedupeCheckResponse,
  LeadDisqualifyRequest,
  LeadId,
  LeadListParams,
  LeadListResponse,
  LeadUpdatePayload,
} from './types';

// ---------------------------------------------------------------------------
// Query keys — colocated so invalidate calls stay in sync with reads.
// ---------------------------------------------------------------------------

export const leadKeys = {
  all: ['leads'] as const,
  lists: () => [...leadKeys.all, 'list'] as const,
  list: (params: LeadListParams) => [...leadKeys.lists(), params] as const,
  detail: (id: LeadId) => [...leadKeys.all, 'detail', id] as const,
  newCount: () => [...leadKeys.all, 'new-count'] as const,
} as const;

const BASE = '/leads';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Strip ``null`` / ``undefined`` / empty-string from a list-params object
 * before handing it to axios. Axios's params serializer stringifies
 * ``null`` to the literal string ``"null"`` which FastAPI then 422s
 * against the typed query params; we'd rather omit the key entirely.
 *
 * (Same helper shape as ``partnerApi.ts`` — kept module-local rather than
 * shared so each module's params type stays self-documenting.)
 */
const stripEmpty = (params: LeadListParams): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' && v.length === 0) continue;
    out[k] = v;
  }
  return out;
};

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export const useLeadList = (
  params: LeadListParams,
): UseQueryResult<LeadListResponse, Error> =>
  useQuery<LeadListResponse, Error>({
    queryKey: leadKeys.list(params),
    queryFn: async () => {
      const { data } = await apiClient.get<LeadListResponse>(BASE, {
        params: stripEmpty(params),
      });
      return data;
    },
    staleTime: 30_000,
  });

export const useLead = (id: LeadId | null): UseQueryResult<Lead, Error> =>
  useQuery<Lead, Error>({
    queryKey: leadKeys.detail(id ?? ''),
    enabled: Boolean(id),
    queryFn: async () => {
      const { data } = await apiClient.get<Lead>(`${BASE}/${id}`);
      return data;
    },
  });

/**
 * Count of leads in the ``new`` status — drives the red nav badge in
 * {@link AppShell}. The BE has no ``count_only`` flag so we hit the list
 * endpoint with ``page_size=1`` and read ``meta.total``; ``staleTime``
 * keeps the badge from spamming the BE while letting it refresh after
 * any focus / refetch event.
 */
export const useNewLeadsBadgeCount = (): UseQueryResult<number, Error> =>
  useQuery<number, Error>({
    queryKey: leadKeys.newCount(),
    queryFn: async () => {
      const { data } = await apiClient.get<LeadListResponse>(BASE, {
        params: { status: 'new', page: 1, page_size: 1 },
      });
      return data.meta.total;
    },
    staleTime: 30_000,
  });

// ---------------------------------------------------------------------------
// Writes — all admin-only on the BE. A 403 surfaces as a normal mutation
// error; callers should show it in their dialog/toast.
// ---------------------------------------------------------------------------

/**
 * Invalidate every list + the new-leads badge after any write. Writes
 * never change a row's identity, so detail caches are updated in-place
 * (``setQueryData``) by the individual mutation handlers below — this
 * helper only blasts the list-level keys.
 */
const invalidateLeadLists = (
  qc: ReturnType<typeof useQueryClient>,
): void => {
  void qc.invalidateQueries({ queryKey: leadKeys.lists() });
  void qc.invalidateQueries({ queryKey: leadKeys.newCount() });
};

export const useCreateLead = (): UseMutationResult<
  Lead,
  Error,
  LeadCreatePayload
> => {
  const qc = useQueryClient();
  return useMutation<Lead, Error, LeadCreatePayload>({
    mutationFn: async (payload) => {
      const { data } = await apiClient.post<Lead>(BASE, payload);
      return data;
    },
    onSuccess: () => {
      invalidateLeadLists(qc);
    },
  });
};

export const useUpdateLead = (): UseMutationResult<
  Lead,
  Error,
  { id: LeadId; payload: LeadUpdatePayload }
> => {
  const qc = useQueryClient();
  return useMutation<Lead, Error, { id: LeadId; payload: LeadUpdatePayload }>({
    mutationFn: async ({ id, payload }) => {
      const { data } = await apiClient.patch<Lead>(`${BASE}/${id}`, payload);
      return data;
    },
    onSuccess: (updated, { id }) => {
      qc.setQueryData(leadKeys.detail(id), updated);
      invalidateLeadLists(qc);
    },
  });
};

export const useSoftDeleteLead = (): UseMutationResult<void, Error, LeadId> => {
  const qc = useQueryClient();
  return useMutation<void, Error, LeadId>({
    mutationFn: async (id) => {
      await apiClient.delete(`${BASE}/${id}`);
    },
    onSuccess: (_void, id) => {
      void qc.invalidateQueries({ queryKey: leadKeys.detail(id) });
      invalidateLeadLists(qc);
    },
  });
};

// ---------------------------------------------------------------------------
// Convert / disqualify — both flip the lead's status, so we re-cache the
// returned row (convert returns the new IDs; disqualify returns the
// updated lead).
// ---------------------------------------------------------------------------

export const useConvertLead = (): UseMutationResult<
  LeadConvertResponse,
  Error,
  { id: LeadId; payload: LeadConvertRequest }
> => {
  const qc = useQueryClient();
  return useMutation<
    LeadConvertResponse,
    Error,
    { id: LeadId; payload: LeadConvertRequest }
  >({
    mutationFn: async ({ id, payload }) => {
      const { data } = await apiClient.post<LeadConvertResponse>(
        `${BASE}/${id}/convert`,
        payload,
      );
      return data;
    },
    onSuccess: (_result, { id }) => {
      // Detail cache is stale after a convert (status -> converted, plus
      // converted_* IDs populated). Drop it and let consumers refetch
      // rather than reconstruct the row client-side.
      void qc.invalidateQueries({ queryKey: leadKeys.detail(id) });
      invalidateLeadLists(qc);
    },
  });
};

export const useDisqualifyLead = (): UseMutationResult<
  Lead,
  Error,
  { id: LeadId; payload: LeadDisqualifyRequest }
> => {
  const qc = useQueryClient();
  return useMutation<
    Lead,
    Error,
    { id: LeadId; payload: LeadDisqualifyRequest }
  >({
    mutationFn: async ({ id, payload }) => {
      const { data } = await apiClient.post<Lead>(
        `${BASE}/${id}/disqualify`,
        payload,
      );
      return data;
    },
    onSuccess: (updated, { id }) => {
      qc.setQueryData(leadKeys.detail(id), updated);
      invalidateLeadLists(qc);
    },
  });
};

// ---------------------------------------------------------------------------
// Dedupe check — pure query against email / phone. Not cached: the SPA
// only hits this from a create / intake dialog and the result is
// short-lived (the user is about to add a row that will mutate the
// answer).
// ---------------------------------------------------------------------------

export const useDedupeCheckLeads = (): UseMutationResult<
  LeadDedupeCheckResponse,
  Error,
  LeadDedupeCheckRequest
> =>
  useMutation<LeadDedupeCheckResponse, Error, LeadDedupeCheckRequest>({
    mutationFn: async (payload) => {
      const { data } = await apiClient.post<LeadDedupeCheckResponse>(
        `${BASE}/dedupe-check`,
        payload,
      );
      return data;
    },
  });
